/**
 * 地理位置语言检测器
 * 根据用户所在国家/地区自动选择合适的语言
 */

// 国家代码到语言的映射
const COUNTRY_LANGUAGE_MAP = {
  // 中文地区
  'CN': 'zh', // 中国大陆
  'TW': 'zh', // 台湾
  'HK': 'zh', // 香港
  'MO': 'zh', // 澳门
  'SG': 'zh', // 新加坡（华人较多）
  
  // 日语地区
  'JP': 'ja', // 日本
  
  // 韩语地区
  'KR': 'ko', // 韩国
  
  // 西班牙语地区
  'ES': 'es', // 西班牙
  'MX': 'es', // 墨西哥
  'AR': 'es', // 阿根廷
  'CO': 'es', // 哥伦比亚
  'PE': 'es', // 秘鲁
  'VE': 'es', // 委内瑞拉
  'CL': 'es', // 智利
  'EC': 'es', // 厄瓜多尔
  'GT': 'es', // 危地马拉
  'CU': 'es', // 古巴
  'BO': 'es', // 玻利维亚
  'DO': 'es', // 多米尼加
  'HN': 'es', // 洪都拉斯
  'PY': 'es', // 巴拉圭
  'SV': 'es', // 萨尔瓦多
  'NI': 'es', // 尼加拉瓜
  'CR': 'es', // 哥斯达黎加
  'PA': 'es', // 巴拿马
  'UY': 'es', // 乌拉圭
  'GQ': 'es', // 赤道几内亚
  
  // 法语地区
  'FR': 'fr', // 法国
  'CA': 'fr', // 加拿大（部分地区）
  'BE': 'fr', // 比利时（部分地区）
  'CH': 'fr', // 瑞士（部分地区）
  'LU': 'fr', // 卢森堡
  'MC': 'fr', // 摩纳哥
  'SN': 'fr', // 塞内加尔
  'CI': 'fr', // 科特迪瓦
  'ML': 'fr', // 马里
  'BF': 'fr', // 布基纳法索
  'NE': 'fr', // 尼日尔
  'TG': 'fr', // 多哥
  'BJ': 'fr', // 贝宁
  'MG': 'fr', // 马达加斯加
  'CM': 'fr', // 喀麦隆
  'CD': 'fr', // 刚果民主共和国
  'CG': 'fr', // 刚果共和国
  'GA': 'fr', // 加蓬
  'CF': 'fr', // 中非共和国
  'TD': 'fr', // 乍得
  'DJ': 'fr', // 吉布提
  'KM': 'fr', // 科摩罗
  'VU': 'fr', // 瓦努阿图
  'NC': 'fr', // 新喀里多尼亚
  'PF': 'fr', // 法属波利尼西亚
  
  // 德语地区
  'DE': 'de', // 德国
  'AT': 'de', // 奥地利
  'LI': 'de', // 列支敦士登
  
  // 俄语地区
  'RU': 'ru', // 俄罗斯
  'BY': 'ru', // 白俄罗斯
  'KZ': 'ru', // 哈萨克斯坦
  'KG': 'ru', // 吉尔吉斯斯坦
  'TJ': 'ru', // 塔吉克斯坦
  'UZ': 'ru', // 乌兹别克斯坦
  'TM': 'ru', // 土库曼斯坦
  'MD': 'ru', // 摩尔多瓦
  
  // 其他所有国家默认使用英语
};

// 支持的语言列表
const SUPPORTED_LANGUAGES = ['en', 'zh', 'ja', 'ko', 'es', 'fr', 'de', 'ru'];

// 默认语言
const DEFAULT_LANGUAGE = 'en';

/**
 * 地理位置语言检测器类
 */
class GeoLanguageDetector {
  constructor() {
    this.cache = new Map();
    this.cacheExpiry = 24 * 60 * 60 * 1000; // 24小时缓存
  }

  /**
   * 检测用户地理位置并返回对应语言
   * @returns {Promise<string>} 语言代码
   */
  async detectLanguage() {
    try {
      // 首先检查缓存
      const cached = this.getCachedResult();
      if (cached) {
        console.log('使用缓存的地理位置语言:', cached);
        return cached;
      }

      // 尝试多种方法检测地理位置
      let countryCode = null;
      
      // 方法1: 使用浏览器时区推测
      countryCode = this.detectByTimezone();
      if (countryCode) {
        console.log('通过时区检测到国家:', countryCode);
      }

      // 方法2: 使用IP地理位置API
      if (!countryCode) {
        countryCode = await this.detectByIP();
        if (countryCode) {
          console.log('通过IP检测到国家:', countryCode);
        }
      }

      // 方法3: 使用浏览器语言作为后备
      if (!countryCode) {
        const browserLang = this.detectByBrowserLanguage();
        console.log('使用浏览器语言作为后备:', browserLang);
        this.setCachedResult(browserLang);
        return browserLang;
      }

      // 根据国家代码获取语言
      const language = this.getLanguageByCountry(countryCode);
      console.log(`国家 ${countryCode} 对应语言:`, language);
      
      // 缓存结果
      this.setCachedResult(language);
      
      return language;
    } catch (error) {
      console.warn('地理位置语言检测失败:', error);
      // 出错时使用浏览器语言作为后备
      return this.detectByBrowserLanguage();
    }
  }

  /**
   * 通过时区推测国家
   * @returns {string|null} 国家代码
   */
  detectByTimezone() {
    try {
      const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
      console.log('检测到时区:', timezone);
      
      // 简单的时区到国家映射
      const timezoneCountryMap = {
        'Asia/Shanghai': 'CN',
        'Asia/Hong_Kong': 'HK',
        'Asia/Taipei': 'TW',
        'Asia/Macau': 'MO',
        'Asia/Tokyo': 'JP',
        'Asia/Seoul': 'KR',
        'Europe/Madrid': 'ES',
        'Europe/Paris': 'FR',
        'Europe/Berlin': 'DE',
        'Europe/Moscow': 'RU',
        'America/New_York': 'US',
        'America/Los_Angeles': 'US',
        'America/Chicago': 'US',
        'America/Denver': 'US',
        'America/Mexico_City': 'MX',
        'America/Argentina/Buenos_Aires': 'AR',
        'America/Bogota': 'CO',
        'America/Lima': 'PE',
        'America/Caracas': 'VE',
        'America/Santiago': 'CL',
      };
      
      return timezoneCountryMap[timezone] || null;
    } catch (error) {
      console.warn('时区检测失败:', error);
      return null;
    }
  }

  /**
   * 通过IP地理位置API检测国家
   * @returns {Promise<string|null>} 国家代码
   */
  async detectByIP() {
    const apis = [
      // 免费的IP地理位置API
      {
        url: 'https://ipapi.co/json/',
        parser: (data) => data.country_code
      },
      {
        url: 'https://ip-api.com/json/',
        parser: (data) => data.countryCode
      },
      {
        url: 'https://ipinfo.io/json',
        parser: (data) => data.country
      },
      {
        url: 'https://api.ipgeolocation.io/ipgeo?apiKey=free',
        parser: (data) => data.country_code2
      }
    ];

    for (const api of apis) {
      try {
        console.log('尝试IP地理位置API:', api.url);
        const response = await fetch(api.url, {
          method: 'GET',
          timeout: 5000, // 5秒超时
        });
        
        if (response.ok) {
          const data = await response.json();
          const countryCode = api.parser(data);
          if (countryCode && typeof countryCode === 'string') {
            console.log('IP API返回国家代码:', countryCode);
            return countryCode.toUpperCase();
          }
        }
      } catch (error) {
        console.warn(`IP API ${api.url} 失败:`, error);
        continue;
      }
    }
    
    return null;
  }

  /**
   * 通过浏览器语言检测
   * @returns {string} 语言代码
   */
  detectByBrowserLanguage() {
    try {
      // 获取浏览器语言列表
      const languages = navigator.languages || [navigator.language || navigator.userLanguage];
      console.log('浏览器语言列表:', languages);
      
      for (const lang of languages) {
        // 提取语言代码（去掉地区代码）
        const langCode = lang.split('-')[0].toLowerCase();
        
        // 检查是否支持该语言
        if (SUPPORTED_LANGUAGES.includes(langCode)) {
          console.log('匹配到支持的浏览器语言:', langCode);
          return langCode;
        }
      }
      
      // 如果没有匹配的语言，返回默认语言
      console.log('未找到匹配的浏览器语言，使用默认语言:', DEFAULT_LANGUAGE);
      return DEFAULT_LANGUAGE;
    } catch (error) {
      console.warn('浏览器语言检测失败:', error);
      return DEFAULT_LANGUAGE;
    }
  }

  /**
   * 根据国家代码获取对应语言
   * @param {string} countryCode 国家代码
   * @returns {string} 语言代码
   */
  getLanguageByCountry(countryCode) {
    const language = COUNTRY_LANGUAGE_MAP[countryCode.toUpperCase()];
    return language || DEFAULT_LANGUAGE;
  }

  /**
   * 获取缓存的结果
   * @returns {string|null} 缓存的语言代码
   */
  getCachedResult() {
    try {
      const cached = localStorage.getItem('geo_language_cache');
      if (cached) {
        const { language, timestamp } = JSON.parse(cached);
        const now = Date.now();
        
        // 检查缓存是否过期
        if (now - timestamp < this.cacheExpiry) {
          return language;
        } else {
          // 清除过期缓存
          localStorage.removeItem('geo_language_cache');
        }
      }
    } catch (error) {
      console.warn('读取地理位置语言缓存失败:', error);
    }
    
    return null;
  }

  /**
   * 设置缓存结果
   * @param {string} language 语言代码
   */
  setCachedResult(language) {
    try {
      const cacheData = {
        language,
        timestamp: Date.now()
      };
      localStorage.setItem('geo_language_cache', JSON.stringify(cacheData));
    } catch (error) {
      console.warn('保存地理位置语言缓存失败:', error);
    }
  }

  /**
   * 清除缓存
   */
  clearCache() {
    try {
      localStorage.removeItem('geo_language_cache');
      this.cache.clear();
      console.log('地理位置语言缓存已清除');
    } catch (error) {
      console.warn('清除地理位置语言缓存失败:', error);
    }
  }

  /**
   * 获取支持的语言列表
   * @returns {Array<string>} 支持的语言代码列表
   */
  getSupportedLanguages() {
    return [...SUPPORTED_LANGUAGES];
  }

  /**
   * 检查是否支持指定语言
   * @param {string} language 语言代码
   * @returns {boolean} 是否支持
   */
  isLanguageSupported(language) {
    return SUPPORTED_LANGUAGES.includes(language);
  }
}

// 创建单例实例
const geoLanguageDetector = new GeoLanguageDetector();

export default geoLanguageDetector; 